import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Surface, Btn } from "./Primitives";
import { CheckCircle, X, FileText, ArrowsClockwise, AirplaneTilt } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * Phase 6A — Superuser pending aircraft type review.
 *
 * Lists every type submitted by a client (`status='pending_review'`).
 * Inline approve / reject. Click "Open" for full edit + general-docs
 * upload at /superuser/aircraft-types/{slug}.
 */
export default function PendingTypesCard() {
  const [pending, setPending] = useState(null);
  const [allCount, setAllCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("pending_review");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const url = filter === "all"
        ? "/superuser/aircraft-types"
        : `/superuser/aircraft-types?status=${filter}`;
      const r = await api.get(url);
      setPending(r.data.types);
      const allR = await api.get("/superuser/aircraft-types");
      setAllCount({
        approved:       allR.data.types.filter(t => t.status === "approved").length,
        pending_review: allR.data.types.filter(t => t.status === "pending_review").length,
        rejected:       allR.data.types.filter(t => t.status === "rejected").length,
      });
    } finally {
      setBusy(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (slug) => {
    await api.post(`/superuser/aircraft-types/${slug}/approve`, {});
    await load();
  };
  const reject = async (slug) => {
    const notes = window.prompt("Reason for rejection (optional):") || "";
    await api.post(`/superuser/aircraft-types/${slug}/reject`, { notes });
    await load();
  };

  if (!pending) return null;

  return (
    <Surface className="p-6" data-testid="pending-types-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-1 flex items-center gap-2">
            <AirplaneTilt size={12} /> Aircraft types catalogue
          </div>
          <div className="text-sm text-zinc-300">
            Review client-submitted types. Approved types are visible to every workspace.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <FilterPill active={filter === "pending_review"} count={allCount?.pending_review} label="Pending" onClick={() => setFilter("pending_review")} testId="filter-pending" />
          <FilterPill active={filter === "approved"} count={allCount?.approved} label="Approved" onClick={() => setFilter("approved")} testId="filter-approved" />
          <FilterPill active={filter === "rejected"} count={allCount?.rejected} label="Rejected" onClick={() => setFilter("rejected")} testId="filter-rejected" />
          <FilterPill active={filter === "all"} count={(allCount?.pending_review||0) + (allCount?.approved||0) + (allCount?.rejected||0)} label="All" onClick={() => setFilter("all")} testId="filter-all" />
          <button onClick={load} disabled={busy} className="text-zinc-500 hover:text-amber" title="Refresh">
            <ArrowsClockwise size={14} />
          </button>
        </div>
      </div>

      {pending.length === 0 ? (
        <div className="text-center py-8 text-zinc-500 text-sm font-mono" data-testid="pending-types-empty">
          No {filter === "all" ? "" : filter.replace("_", " ")} types.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono border-b border-zinc-900">
              <th className="text-left py-2 pr-3">Type</th>
              <th className="text-left py-2 pr-3">Submitted by</th>
              <th className="text-left py-2 pr-3">Status</th>
              <th className="text-left py-2 pr-3">General docs</th>
              <th className="text-right py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="pending-types-table">
            {pending.map((t) => (
              <tr key={t.slug} className="border-b border-zinc-900/60 hover:bg-zinc-950/40">
                <td className="py-3 pr-3">
                  <div className="font-medium">{t.manufacturer} {t.model}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{t.slug} · {t.fuel_type}</div>
                </td>
                <td className="py-3 pr-3 text-xs font-mono">
                  {t._submitted_by_email || <span className="text-zinc-600">system_seed</span>}
                  {t._submitted_by_tenant_name && (
                    <div className="text-[10px] text-zinc-500">{t._submitted_by_tenant_name}</div>
                  )}
                </td>
                <td className="py-3 pr-3">
                  <StatusBadge status={t.status} />
                </td>
                <td className="py-3 pr-3 text-xs font-mono text-zinc-400">
                  <FileText size={12} className="inline mr-1" />
                  {(t.general_docs || []).length}
                </td>
                <td className="py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Link to={`/superuser/aircraft-types/${t.slug}`}
                          className="text-xs text-amber hover:underline"
                          data-testid={`open-type-${t.slug}`}>Open →</Link>
                    {t.status === "pending_review" && (
                      <>
                        <button onClick={() => approve(t.slug)}
                                className="text-xs text-emerald-300 hover:underline"
                                data-testid={`approve-type-${t.slug}`}>
                          <CheckCircle size={12} className="inline" /> Approve
                        </button>
                        <button onClick={() => reject(t.slug)}
                                className="text-xs text-red-300 hover:underline"
                                data-testid={`reject-type-${t.slug}`}>
                          <X size={12} className="inline" /> Reject
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Surface>
  );
}

function FilterPill({ active, count, label, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "text-[10px] uppercase tracking-wider font-mono px-2.5 py-1 border " +
        (active ? "border-amber text-amber bg-amber/5" : "border-zinc-800 text-zinc-400 hover:text-zinc-200")
      }
    >
      {label}{count != null ? ` (${count})` : ""}
    </button>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    approved:       { c: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5", l: "APPROVED" },
    pending_review: { c: "border-amber/40 text-amber bg-amber/5",                   l: "PENDING REVIEW" },
    rejected:       { c: "border-red-500/40 text-red-300 bg-red-500/5",             l: "REJECTED" },
  }[status] || { c: "border-zinc-800 text-zinc-400", l: status };
  return <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 border ${cfg.c}`}>{cfg.l}</span>;
}
